import { Lock, ExternalLink } from "lucide-react";
import type { LucideIcon } from "lucide-react";

interface DocumentCardProps {
  name: string;
  icon: LucideIcon;
  available: boolean;
  isInteractive: boolean;
  fileUrl?: string;
  color:
    | "blue"
    | "purple"
    | "green"
    | "orange"
    | "pink"
    | "indigo"
    | "gray"
    | "red";
  onClick?: () => void;
}

const colorClasses = {
  blue: "from-blue-400 to-blue-600",
  purple: "from-purple-400 to-purple-600",
  green: "from-green-400 to-green-600",
  orange: "from-orange-400 to-orange-600",
  pink: "from-pink-400 to-pink-600",
  indigo: "from-indigo-400 to-indigo-600",
  gray: "from-gray-400 to-gray-600",
  red: "from-red-400 to-red-600",
};

export default function DocumentCard({
  name,
  icon: Icon,
  available,
  isInteractive,
  fileUrl,
  color,
  onClick,
}: DocumentCardProps) {
  const baseClasses = `
    p-3 sm:p-4 rounded-lg border-2 border-black transition-all text-left w-full
    ${
      available
        ? `bg-gradient-to-r ${colorClasses[color]} text-white hover:translate-y-[-2px] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]`
        : "bg-white text-gray-400 cursor-not-allowed"
    }
  `;

  const content = (
    <>
      <div className="flex items-center justify-between mb-2 sm:mb-3">
        <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
        <div className="flex items-center gap-2">
          {!isInteractive && available && (
            <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4" />
          )}
          {!available && <Lock className="w-3 h-3 sm:w-4 sm:h-4" />}
        </div>
      </div>
      <div className="mb-2">
        <span
          className={`inline-block px-2 py-0.5 text-xs font-bold rounded ${
            available
              ? isInteractive
                ? "bg-white/30 text-white"
                : "bg-black/20 text-white"
              : "bg-gray-200 text-gray-500"
          }`}
        >
          {isInteractive ? "Interactive" : "PDF"}
        </span>
      </div>
      <div
        className={`font-bold text-sm sm:text-base ${
          available ? "text-white" : "text-gray-400"
        }`}
      >
        {name}
      </div>
      {!available && (
        <div className="text-xs mt-1 sm:mt-2 text-gray-500">
          Access Restricted
        </div>
      )}
    </>
  );

  // PDF Document - open in new tab
  if (!isInteractive && fileUrl && available) {
    return (
      <a
        href={fileUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={baseClasses}
      >
        {content}
      </a>
    );
  }

  // Interactive Document or unavailable
  return (
    <button
      onClick={available ? onClick : undefined}
      disabled={!available}
      className={baseClasses}
    >
      {content}
    </button>
  );
}
